import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import { LoadingState } from '../../components/ui/States';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import apiClient from '../../api/client';

/**
 * Rules mirrored from src/utils/passwordPolicy.js on the backend, which is the
 * single enforcement point. Kept here only so the user is told what is wrong
 * before submitting, never as the authority.
 */
const RULES: { label: string; test: (p: string) => boolean }[] = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'An uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'A lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'A number', test: (p) => /[0-9]/.test(p) },
  { label: 'A special character', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
];

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [emailOnToken, setEmailOnToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    const validate = async () => {
      if (!token) {
        setError('This reset link is missing its token. Use the link from your email.');
        setValidating(false);
        return;
      }
      try {
        const res: any = await apiClient.get(`/api/auth/reset-password/${token}`);
        setTokenValid(Boolean(res?.valid));
        setEmailOnToken(res?.email || '');
      } catch (err: any) {
        setError(
          err.response?.data?.message ||
            'This reset link is invalid or has expired. Request a new one.'
        );
      } finally {
        setValidating(false);
      }
    };
    validate();
  }, [token]);

  const unmetRules = RULES.filter((r) => !r.test(password));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (unmetRules.length > 0) {
      setError(`Password still needs: ${unmetRules.map((r) => r.label.toLowerCase()).join(', ')}.`);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post('/api/auth/reset-password', { token, password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not reset your password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (validating) {
    return (
      <AuthLayout title="Checking your link…">
        <LoadingState label="One moment." height={120} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password">

          {done ? (
            <>
              <Alert severity="success" sx={{ my: 2 }}>
                Your password has been reset. Taking you to sign in…
              </Alert>
              <Button component={RouterLink} to="/login" variant="contained" fullWidth>
                Sign in now
              </Button>
            </>
          ) : !tokenValid ? (
            <>
              <Alert severity="error" sx={{ my: 2 }}>
                {error || 'This reset link is invalid or has expired.'}
              </Alert>
              <Button component={RouterLink} to="/forgot-password" variant="contained" fullWidth>
                Request a new link
              </Button>
            </>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              {emailOnToken && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Setting a new password for <strong>{emailOnToken}</strong>.
                </Typography>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                label="New password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                autoFocus
                autoComplete="new-password"
                disabled={submitting}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ mb: 2 }}>
                {RULES.map((rule) => {
                  const met = rule.test(password);
                  return (
                    <Typography
                      key={rule.label}
                      variant="caption"
                      display="block"
                      color={met ? 'success.main' : 'text.secondary'}
                    >
                      {met ? '✓' : '○'} {rule.label}
                    </Typography>
                  );
                })}
              </Box>

              <TextField
                label="Confirm new password"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                autoComplete="new-password"
                disabled={submitting}
                error={confirmPassword.length > 0 && confirmPassword !== password}
                helperText={
                  confirmPassword.length > 0 && confirmPassword !== password
                    ? 'Passwords do not match'
                    : ' '
                }
                sx={{ mb: 2 }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={submitting || unmetRules.length > 0 || password !== confirmPassword}
              >
                {submitting ? <CircularProgress size={24} /> : 'Reset password'}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link component={RouterLink} to="/login" variant="body2">
                  Back to sign in
                </Link>
              </Box>
            </Box>
          )}
    </AuthLayout>
  );
};

export default ResetPassword;
